import { chain, createToggler, enable } from '@leprecoins/effector';
import { combine, createStore, sample } from 'effector';
import { and, condition, debounce, equals, spread } from 'patronum';
import {
	GAME_FIELD_COLUMNS,
	GAME_FIELD_ROWS,
	GameCellVariant,
	gameModel,
	ONBOARDING_STEPS,
} from 'entities/game';
import {
	receiveBet,
	receiveRefresh,
	receiveTurn,
	sendBet,
	sendTurn,
	TURN_STATUSES,
} from 'shared/api';
import { cashoutSound, winSound, loseSound } from 'shared/lib';
import { GAME_FIELD_GHOST_DURATION, GAME_FIELD_DISABLED_DELAY } from '../config';

const DEFAULT_CELLS = Array.from<GameCellVariant[]>({ length: GAME_FIELD_ROWS }).fill(
	Array.from<GameCellVariant>({ length: GAME_FIELD_COLUMNS }).fill('default'),
);

const $cells = createStore(DEFAULT_CELLS);

const $turnPending = createStore(false);

const $turnMultiplier = createStore<number>(0);

const $turnHistory = createStore<number[]>([]);

const $mines = createStore<number[][]>([]);

const $turnRow = createStore(-1);

const $enableOnboarding = and(
	gameModel.$isOnboarding,
	equals(gameModel.$onboardingStep, ONBOARDING_STEPS.GAME_FIELD),
);

const $multipliersData = combine({
	turnStatus: gameModel.$turnStatus,
	multipliers: gameModel.$multipliers,
	turnMultiplier: $turnMultiplier,
});

const fieldDisabledToggler = createToggler();

condition({
	source: gameModel.$isOnboarding,
	if: (isOnboarding) => isOnboarding,
	then: fieldDisabledToggler.enable,
	else: fieldDisabledToggler.disable,
});

sample({
	clock: [sendBet, receiveBet.failure, gameModel.receiveInsufficientBalance],
	target: [$turnRow.reinit, $turnMultiplier.reinit, $turnHistory.reinit, $mines.reinit],
});

sample({
	clock: receiveBet.success,
	fn: () => 0,
	target: $turnRow,
});

enable(sendTurn, $turnPending);

sample({
	clock: [receiveTurn.success, receiveRefresh.success],
	source: $turnRow,
	fn: (turnRow, turn) => ({
		turnPending: false,
		turnRow: turn.status === TURN_STATUSES.bet ? turn.turnHistory.length : turnRow,
		...turn,
	}),
	target: spread({
		turnPending: $turnPending,
		turnRow: $turnRow,
		multiplier: $turnMultiplier,
		turnHistory: $turnHistory,
		mines: $mines,
	}),
});

sample({
	clock: combine({
		turnStatus: gameModel.$turnStatus,
		turnRow: $turnRow,
		turnMultiplier: $turnMultiplier,
		turnHistory: $turnHistory,
		mines: $mines,
		turnPending: $turnPending,
	}),
	source: $cells,
	fn: (cells, { turnStatus, turnRow, turnHistory, mines, turnPending }) =>
		cells.map((row, ri) =>
			row.map((_, ci) => {
				const isInHistory = turnHistory ? turnHistory[ri] === ci : false;

				const isChoice = !turnPending && turnStatus === TURN_STATUSES.bet && ri === turnRow;

				const isMistake =
					turnStatus === TURN_STATUSES.lose && turnRow === ri && turnHistory[turnRow] === ci;

				const isMine = mines[ri]?.includes(ci) ?? false;

				const isEnd = turnStatus === TURN_STATUSES.lose || turnStatus === TURN_STATUSES.win;

				if (isMistake) return 'mistake';

				if (isEnd && !isInHistory && !isMine) return 'ghost';

				if (isChoice) return 'choice';

				if (isInHistory) return 'ball';

				return 'default';
			}),
		),
	target: $cells,
});

sample({
	clock: debounce(
		gameModel.$turnStatus,
		GAME_FIELD_GHOST_DURATION * GAME_FIELD_ROWS + GAME_FIELD_DISABLED_DELAY,
	),
	source: gameModel.$turnStatus,
	filter: (status) => status === TURN_STATUSES.lose || status === TURN_STATUSES.win,
	target: fieldDisabledToggler.enable,
});

sample({
	clock: gameModel.$turnStatus,
	filter: (status) => status === TURN_STATUSES.bet,
	target: fieldDisabledToggler.disable,
});

sample({
	clock: gameModel.$turnStatus,
	source: $turnHistory,
	filter: (history, status) => history?.length < GAME_FIELD_ROWS && status === TURN_STATUSES.win,
	target: cashoutSound.play,
});

sample({
	clock: gameModel.$turnStatus,
	source: $turnHistory,
	filter: (history, status) => history?.length === GAME_FIELD_ROWS && status === TURN_STATUSES.win,
	target: [winSound.play, gameModel.win],
});

sample({
	clock: gameModel.$turnStatus,
	filter: (status) => status === TURN_STATUSES.lose,
	target: loseSound.play,
});

chain(fieldDisabledToggler.enabled, gameModel.gameFieldAnimated);

export const gameFieldModel = {
	$cells,
	$multipliersData,
	fieldDisabledToggler,
	$enableOnboarding,
};
